import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, CircularProgress, Divider, IconButton, TextField, Typography } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import LinkOff from "@mui/icons-material/LinkOff";
import SendOutlined from "@mui/icons-material/SendOutlined";
import LockOpenOutlined from "@mui/icons-material/LockOpenOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import { MAX_MESSAGE_LEN } from "../../services/schemas/adminTicketSchemas";
import { formatTicketDate } from "../../services/adminTicketUtils";
import { useUI } from "../Context/UIContext";
import { useAdminTicketUnread } from "../Context/ticketUnreadContext";
import TicketChatMessage from "./TicketChatMessage";
import TicketChatSkeleton from "./TicketChatSkeleton";
import {
  chatDividerSx,
  chatInputSx,
  chatPaginationChipSx,
  chatThreadSx,
  roundActionIconSx,
} from "./ticketChatConstants";
import { useAdminTicketChat } from "./useAdminTicketChat";

// Subcomponents for reduced cognitive complexity
function MessageThread({ chat, renderMessage }) {
  return (
    <Box ref={chat.threadRef} sx={chatThreadSx}>
      {chat.loading ? (
        <TicketChatSkeleton />
      ) : (
        <>
          {chat.descriptionMessage && renderMessage(chat.descriptionMessage)}
          {chat.descriptionMessage && chat.hasChatMessages && (
            <Divider sx={chatDividerSx} />
          )}
          {chat.hasChatMessages && chat.showPaginationChip && (
            <Box display="flex" justifyContent="center" my={0.5}>
              <Chip
                label={chat.loadMoreLabel}
                onClick={chat.canLoadMore ? chat.handleLoadMore : undefined}
                disabled={chat.loadingMore || !chat.canLoadMore}
                variant="outlined"
                size="small"
                sx={chatPaginationChipSx}
              />
            </Box>
          )}
          {chat.visibleMessages.map((msg) => renderMessage(msg))}
        </>
      )}
    </Box>
  );
}

MessageThread.propTypes = {
  chat: PropTypes.shape({
    threadRef: PropTypes.oneOfType([
      // Either a function or a ref object
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.any }),
    ]),
    loading: PropTypes.bool,
    descriptionMessage: PropTypes.object,
    hasChatMessages: PropTypes.bool,
    showPaginationChip: PropTypes.bool,
    loadMoreLabel: PropTypes.string,
    canLoadMore: PropTypes.bool,
    handleLoadMore: PropTypes.func,
    loadingMore: PropTypes.bool,
    visibleMessages: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  renderMessage: PropTypes.func.isRequired,
};

function MessageHeader({ chat }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87", fontSize: "0.875rem" }}>
          {chat.isEditing ? "Modifica messaggio" : "Messaggio"}
        </Typography>
        {chat.isEditing && (
          <Typography
            component="button"
            type="button"
            onClick={chat.resetComposer}
            sx={{
              fontFamily: "Rubik",
              fontSize: "0.75rem",
              color: "#808080",
              background: "none",
              border: "none",
              cursor: "pointer",
              p: 0,
              textDecoration: "underline",
            }}
          >
            Annulla
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontFamily: "Rubik", color: "#808080", fontSize: "0.875rem" }}>
        {chat.messageText.length}/{MAX_MESSAGE_LEN}
      </Typography>
    </Box>
  );
}

MessageHeader.propTypes = {
  chat: PropTypes.shape({
    isEditing: PropTypes.bool,
    resetComposer: PropTypes.func,
    messageText: PropTypes.string.isRequired,
  }).isRequired,
};

function MessageActions({ chat, messageField }) {
  return (
    <Box display="flex" alignItems="flex-end" gap={1} mb={1}>
      {chat.readOnly ? (
        <Tooltip title={chat.disabledTooltip} placement="top" arrow>
          <Box sx={{ flex: 1, opacity: 0.75 }}>{messageField}</Box>
        </Tooltip>
      ) : (
        messageField
      )}
      <Box mt={1} display="flex" alignItems="center" gap={1}>
        <AttachmentAction chat={chat} />
        <SendAction chat={chat} />
        <CloseOrReopenActions chat={chat} />
      </Box>
    </Box>
  );
}

MessageActions.propTypes = {
  chat: PropTypes.shape({
    readOnly: PropTypes.bool,
    disabledTooltip: PropTypes.string,
  }).isRequired,
  messageField: PropTypes.node.isRequired,
};

function AttachmentAction({ chat }) {
  return (
    <Tooltip title={chat.showAttachmentRemove ? "Rimuovi allegato" : "Allegato"} placement="top">
      <span>
        {chat.showAttachmentRemove ? (
          <IconButton
            disabled={chat.readOnly || chat.sending}
            onClick={chat.handleRemoveAttachment}
            sx={roundActionIconSx}
          >
            <LinkOff sx={{ color: "#e00036", transform: "rotate(90deg)" }} />
          </IconButton>
        ) : (
          <IconButton
            component="label"
            disabled={chat.readOnly || chat.sending}
            sx={roundActionIconSx}
          >
            <AttachFileOutlined />
            <input
              ref={chat.fileInputRef}
              type="file"
              hidden
              accept="image/jpeg,image/png,application/pdf"
              onChange={chat.handleFileChange}
            />
          </IconButton>
        )}
      </span>
    </Tooltip>
  );
}

AttachmentAction.propTypes = {
  chat: PropTypes.shape({
    showAttachmentRemove: PropTypes.bool,
    readOnly: PropTypes.bool,
    sending: PropTypes.bool,
    handleRemoveAttachment: PropTypes.func,
    fileInputRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.any }),
    ]),
    handleFileChange: PropTypes.func,
  }).isRequired,
};

function SendAction({ chat }) {
  return (
    <Tooltip title={chat.isEditing ? "Salva modifiche" : "Invia"} placement="top">
      <span>
        <IconButton
          disabled={chat.readOnly || chat.sending || !chat.canSubmit}
          onClick={chat.handleSend}
          sx={roundActionIconSx}
        >
          {chat.sending ? <CircularProgress size={20} /> : <SendOutlined />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

SendAction.propTypes = {
  chat: PropTypes.shape({
    isEditing: PropTypes.bool,
    readOnly: PropTypes.bool,
    sending: PropTypes.bool,
    canSubmit: PropTypes.bool,
    handleSend: PropTypes.func,
  }).isRequired,
};

function CloseOrReopenActions({ chat }) {
  return (
    <>
      {chat.showClose && (
        <Tooltip title="Chiudi ticket" placement="top">
          <span>
            <IconButton onClick={chat.handleClose} sx={roundActionIconSx}>
              <CheckOutlined sx={{ color: "success.main" }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {chat.showReopen && (
        <Tooltip title="Riapri ticket" placement="top">
          <span>
            <IconButton onClick={chat.handleReopen} sx={roundActionIconSx}>
              <LockOpenOutlined />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );
}

CloseOrReopenActions.propTypes = {
  chat: PropTypes.shape({
    showClose: PropTypes.bool,
    handleClose: PropTypes.func,
    showReopen: PropTypes.bool,
    handleReopen: PropTypes.func,
  }).isRequired,
};

function AttachmentHint({ chat }) {
  if (!chat.pendingAttachmentLabel && !chat.attachmentError) return null;
  return (
    <>
      {chat.pendingAttachmentLabel && (
        <Typography variant="caption" sx={{ display: "block", fontFamily: "Rubik", color: "#808080" }}>
          Allegato: {chat.pendingAttachmentLabel}
        </Typography>
      )}
      {chat.attachmentError && (
        <Typography variant="caption" color="error.main" sx={{ display: "block", fontFamily: "Rubik" }}>
          {chat.attachmentError}
        </Typography>
      )}
    </>
  );
}

AttachmentHint.propTypes = {
  chat: PropTypes.shape({
    pendingAttachmentLabel: PropTypes.string,
    attachmentError: PropTypes.string,
  }).isRequired,
};

const TicketChat = ({
  ticket,
  loginId,
  isAdmin,
  onTicketUpdated,
  onMessagesRead,
}) => {
  const { showError, showSuccess } = useUI();
  const { refreshSummary } = useAdminTicketUnread();
  const chat = useAdminTicketChat({
    ticket,
    loginId,
    isAdmin,
    onTicketUpdated,
    onMessagesRead,
    refreshSummary,
    showError,
    showSuccess,
  });

  const renderMessage = (msg) => {
    const isOwn = Boolean(msg.operator);
    return (
      <TicketChatMessage
        key={msg.id}
        msg={msg}
        isOwn={isOwn}
        onEditStart={chat.handleEditStart}
        onDelete={chat.handleDelete}
        onDownload={chat.handleDownload}
        showEditActions={Boolean(msg.editable && chat.chatWritable && !msg.isDescription && !chat.isEditing)}
        showReadReceipt={isOwn && !msg.isDescription}
        formatDate={formatTicketDate}
      />
    );
  };

  const messageField = (
    <TextField
      multiline
      minRows={1}
      maxRows={4}
      fullWidth
      size="small"
      placeholder={chat.isEditing ? "Modifica messaggio" : "Scrivi un messaggio"}
      value={chat.messageText}
      onChange={(e) => chat.setMessageText(e.target.value)}
      onKeyDown={chat.handleKeyDown}
      disabled={chat.readOnly || chat.sending}
      slotProps={{ htmlInput: { maxLength: MAX_MESSAGE_LEN } }}
      sx={chatInputSx}
    />
  );

  return (
    <Box>
      <MessageThread chat={chat} renderMessage={renderMessage} />
      <MessageHeader chat={chat} />
      <MessageActions chat={chat} messageField={messageField} />
      <AttachmentHint chat={chat} />
    </Box>
  );
};

TicketChat.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string,
    createdAt: PropTypes.any,
    updatedAt: PropTypes.any,
    description: PropTypes.string,
  }).isRequired,
  loginId: PropTypes.number,
  isAdmin: PropTypes.bool,
  onTicketUpdated: PropTypes.func,
  onMessagesRead: PropTypes.func,
};

TicketChat.defaultProps = {
  loginId: null,
  isAdmin: false,
  onTicketUpdated: null,
  onMessagesRead: null,
};

export default TicketChat;
 